import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  MobileStepper, Paper, Stack, Typography
} from '@mui/material';
import {
  AutoAwesome, CloudUpload, HelpOutline, Publish, RestaurantMenu, Settings,
  Storefront, Tune, TableRestaurant, TwoWheeler, Payments, HealthAndSafety
} from '@mui/icons-material';
import { useAuth } from '../services/AuthService';

const labels = {
  account: 'Conta criada',
  logo: 'Adicionar logo',
  schedule: 'Configurar horario',
  delivery: 'Configurar entrega',
  payment: 'Conectar pagamento',
  printing: 'Configurar impressao',
  products: 'Cadastrar produtos',
  test_order: 'Fazer pedido de teste'
};

const tutorialSteps = [
  {
    icon: <AutoAwesome sx={{ fontSize: 42 }} />,
    title: 'Bem-vindo ao All Delivery!',
    text: 'Vamos preparar sua loja juntos. São apenas três etapas: configurar a loja, cadastrar produtos e publicar.'
  },
  {
    icon: <Settings sx={{ fontSize: 42 }} />,
    title: '1. Configure sua loja',
    text: 'Clique em “Abrir configurações”. Depois entre em “Editar dados” para adicionar logo, horário, endereço e taxa de entrega.',
    target: 'settings'
  },
  {
    icon: <RestaurantMenu sx={{ fontSize: 42 }} />,
    title: '2. Cadastre os produtos',
    text: 'Use um arquivo CSV, envie PDF ou foto para revisão, ou clique em “Cadastrar produtos manualmente”. É necessário ter pelo menos um produto.',
    target: 'products'
  },
  {
    icon: <Tune sx={{ fontSize: 42 }} />,
    title: '3. Configure adicionais',
    text: 'Crie grupos como tamanho, sabores e complementos. Marque as escolhas obrigatorias para o cliente nao concluir um item incompleto.',
    path: '/admin/product-options'
  },
  {
    icon: <TableRestaurant sx={{ fontSize: 42 }} />,
    title: '4. Prepare as mesas',
    text: 'Abra a Gestao de Mesas, confira os numeros e teste um pedido como garcom antes de atender clientes.',
    path: '/admin/mesas'
  },
  {
    icon: <TwoWheeler sx={{ fontSize: 42 }} />,
    title: '5. Cadastre os motoboys',
    text: 'Defina se a entrega usa motoboy da loja ou do aplicativo e mantenha telefone e disponibilidade atualizados.',
    path: '/admin/motoboys-management'
  },
  {
    icon: <Payments sx={{ fontSize: 42 }} />,
    title: '6. Configure pagamentos',
    text: 'Conecte Mercado Pago e confira Pix, cartao, dinheiro e retirada. Faca um pagamento pequeno de teste antes de abrir.',
    path: '/admin/admin-lanchonete'
  },
  {
    icon: <HealthAndSafety sx={{ fontSize: 42 }} />,
    title: '7. Teste o sistema',
    text: 'Na Saude do sistema, teste som, notificacao, impressora e conexoes. Corrija qualquer alerta antes do primeiro pedido.',
    path: '/admin/operations'
  },
  {
    icon: <Publish sx={{ fontSize: 42 }} />,
    title: '8. Publique sua loja',
    text: 'Quando logo, horário e produto estiverem prontos, clique em “Publicar minha loja”. Só então ela aparecerá para os clientes.',
    target: 'publish'
  }
];

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const separator = lines[0].includes(';') ? ';' : ',';
  const cells = (line) => line.split(separator).map((item) => item.trim().replace(/^"|"$/g, ''));
  const first = cells(lines[0]).map((item) => item.toLowerCase());
  const hasHeader = first.some((item) => ['nome', 'name', 'produto', 'preco', 'price'].includes(item));
  const indexes = {
    name: Math.max(0, first.findIndex((item) => ['nome', 'name', 'produto'].includes(item))),
    price: first.findIndex((item) => ['preco', 'preço', 'price', 'valor'].includes(item)),
    description: first.findIndex((item) => ['descricao', 'descrição', 'description'].includes(item)),
    category: first.findIndex((item) => ['categoria', 'category'].includes(item))
  };
  return lines.slice(hasHeader ? 1 : 0).map((line) => {
    const row = cells(line);
    return {
      name: row[indexes.name] || '',
      price: row[indexes.price >= 0 ? indexes.price : 1] || '',
      description: indexes.description >= 0 ? row[indexes.description] : '',
      category: indexes.category >= 0 ? row[indexes.category] : 'Cardapio'
    };
  }).filter((item) => item.name && item.price);
};

export default function StoreOnboarding() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [selectedCsvName, setSelectedCsvName] = useState('');
  const [missing, setMissing] = useState([]);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialInitialized, setTutorialInitialized] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/onboarding/status');
    setStatus(data);
  }, [api]);

  useEffect(() => { load().catch(() => setMessage({ severity: 'error', text: 'Nao foi possivel carregar o assistente.' })); }, [load]);

  useEffect(() => {
    if (!status || tutorialInitialized) return;
    setTutorialInitialized(true);
    if (!status.onboarding_checklist?.tutorial_completed && status.onboarding_status !== 'published') {
      setTutorialOpen(true);
    }
  }, [status, tutorialInitialized]);

  const checklist = useMemo(() => {
    const base = status?.onboarding_checklist || {};
    return { ...base, products: (status?.product_count || 0) > 0 || base.products };
  }, [status]);
  const progress = Math.round((
    Object.keys(labels).filter((key) => Boolean(checklist[key])).length /
    Object.keys(labels).length
  ) * 100);

  const selectCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const parsed = parseCsv(await file.text());
    setSelectedCsvName(file.name);
    setProducts(parsed);
    setMessage(parsed.length
      ? { severity: 'success', text: `${parsed.length} produto(s) encontrado(s) em ${file.name}. Revise e confirme a importação abaixo.` }
      : { severity: 'warning', text: `Não encontrei produtos válidos em ${file.name}. Use as colunas nome, preco, descricao e categoria.` });
  };

  const downloadCsvTemplate = () => {
    const content = 'nome;preco;descricao;categoria\nX-Burguer;18,90;Pao carne queijo e salada;Lanches\nRefrigerante;6,00;Lata 350ml;Bebidas\n';
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'modelo-cardapio-all-delivery.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importProducts = async () => {
    setBusy('products');
    try {
      const { data } = await api.post('/onboarding/products/import', { products });
      setMessage({ severity: 'success', text: data.message });
      setProducts([]);
      setSelectedCsvName('');
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.error || 'Falha na importacao.' });
    } finally {
      setBusy('');
    }
  };

  const uploadMenu = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy('file');
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post('/onboarding/menu-file', form);
      setMessage({
        severity: 'info',
        text: `${file.name} foi recebido. PDF e foto entram na fila de importação assistida e não criam produtos automaticamente. Você ainda precisa aguardar a revisão ou cadastrar/importar os produtos por CSV.`
      });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.error || 'Falha ao enviar cardapio.' });
    } finally {
      setBusy('');
    }
  };

  const publish = async () => {
    setBusy('publish');
    try {
      const { data } = await api.post('/onboarding/publish');
      setMessage({ severity: 'success', text: data.message });
      setMissing([]);
      await load();
    } catch (error) {
      const required = error.response?.data?.missing || [];
      setMissing(required);
      setMessage({ severity: 'warning', text: error.response?.data?.error || 'Ainda faltam etapas.' });
    } finally {
      setBusy('');
    }
  };

  const finishTutorial = async (navigateToSettings = false) => {
    try {
      await api.patch('/onboarding/checklist', { tutorial_completed: true });
      setStatus((current) => ({
        ...current,
        onboarding_checklist: {
          ...(current?.onboarding_checklist || {}),
          tutorial_completed: true
        }
      }));
    } catch (_) {
      // Se a rede falhar, o tutorial volta na proxima visita para nao deixar o dono sem orientacao.
    }
    setTutorialOpen(false);
    setTutorialStep(0);
    if (navigateToSettings) navigate('/admin/admin-lanchonete?from=onboarding');
  };

  const tutorialTargetStyle = (target) => tutorialOpen && tutorialSteps[tutorialStep]?.target === target
    ? {
        outline: '4px solid #E50914',
        outlineOffset: '5px',
        boxShadow: '0 0 0 8px rgba(255,107,53,.14), 0 20px 45px rgba(15,23,42,.15)',
        position: 'relative',
        zIndex: 2
      }
    : {};

  if (!status) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={900}>Vamos colocar {status.name} no ar</Typography>
        <Typography color="text.secondary">Ola, {user?.name}. Complete o essencial e publique quando estiver pronto.</Typography>
        <Paper sx={{ p: 3, my: 3, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={800}>Progresso da configuracao</Typography>
            <Chip label={`${progress}%`} color={progress === 100 ? 'success' : 'primary'} />
          </Stack>
          <LinearProgress variant="determinate" value={progress} sx={{ my: 2, height: 10, borderRadius: 5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
            {Object.entries(labels).map(([key, label]) => (
              <Stack direction="row" alignItems="center" key={key}>
                <Checkbox checked={Boolean(checklist[key])} disabled /><Typography variant="body2">{label}</Typography>
              </Stack>
            ))}
          </Box>
        </Paper>
        {message && <Alert severity={message.severity} sx={{ mb: 3 }}>{message.text}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1.2fr 1fr' }, gap: 3 }}>
          <Card sx={tutorialTargetStyle('products')}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center"><RestaurantMenu color="primary" /><Typography variant="h6" fontWeight={800}>Primeiros produtos</Typography></Stack>
              <Typography color="text.secondary" sx={{ my: 2 }}>
                Importe CSV com colunas nome, preco, descricao e categoria. Revise a previa antes de confirmar.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                Escolher CSV<input hidden type="file" accept=".csv,text/csv" onChange={selectCsv} />
              </Button>
              <Button variant="text" onClick={downloadCsvTemplate}>Baixar modelo CSV</Button>
              </Stack>
              {selectedCsvName && <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>Arquivo selecionado: {selectedCsvName}</Typography>}
              {products.length > 0 && <Box sx={{ mt: 2 }}>
                <Typography fontWeight={700}>{products.length} produto(s) encontrado(s)</Typography>
                <Box sx={{ maxHeight: 260, overflow: 'auto', my: 1 }}>
                  {products.slice(0, 50).map((item, index) => (
                    <Stack direction="row" justifyContent="space-between" key={`${item.name}-${index}`} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                      <Typography>{item.name}</Typography><Typography fontWeight={700}>R$ {item.price}</Typography>
                    </Stack>
                  ))}
                </Box>
                <Button variant="contained" onClick={importProducts} disabled={busy === 'products'}>Importar produtos</Button>
              </Box>}
              <Typography fontWeight={700} sx={{ mt: 4 }}>Tem apenas PDF ou foto?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Envie para importação assistida. O arquivo fica aguardando revisão e não cria produtos automaticamente.
              </Typography>
              <Button component="label" variant="outlined" disabled={busy === 'file'}>
                {busy === 'file' ? 'Enviando...' : 'Enviar PDF ou foto'}<input hidden type="file" accept=".pdf,image/jpeg,image/png,image/webp" onChange={uploadMenu} />
              </Button>
              {(status.menu_imports || []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight={800}>Arquivos enviados</Typography>
                  {(status.menu_imports || []).slice(0, 3).map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" sx={{ mt: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: '70%' }}>{item.file_name}</Typography>
                      <Chip size="small" label={item.status === 'pending' ? 'Aguardando revisão' : item.status} color={item.status === 'pending' ? 'warning' : 'default'} />
                    </Stack>
                  ))}
                </Box>
              )}
              <Button sx={{ mt: 2, ml: { sm: 1 } }} onClick={() => navigate('/admin/produtos')}>
                Cadastrar produtos manualmente
              </Button>
            </CardContent>
          </Card>
          <Stack spacing={3}>
            <Card sx={tutorialTargetStyle('settings')}><CardContent>
              <Stack direction="row" spacing={1}><Settings color="primary" /><Typography variant="h6" fontWeight={800}>Configurar loja</Typography></Stack>
              <Typography color="text.secondary" sx={{ my: 2 }}>Adicione logo, horario, entrega e pagamento no painel da loja.</Typography>
              <Button variant="contained" onClick={() => navigate('/admin/admin-lanchonete?from=onboarding')}>Abrir configurações</Button>
            </CardContent></Card>
            <Card sx={tutorialTargetStyle('publish')}><CardContent>
              <Stack direction="row" spacing={1}><Publish color="primary" /><Typography variant="h6" fontWeight={800}>Publicar</Typography></Stack>
              <Typography color="text.secondary" sx={{ my: 2 }}>A loja fica invisivel aos clientes ate ter logo, horario e pelo menos um produto.</Typography>
              {missing.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={800}>Ainda falta:</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {missing.map((item) => <Chip key={item} size="small" label={item} />)}
                  </Stack>
                </Alert>
              )}
              <Button fullWidth variant="contained" color="success" onClick={publish} disabled={busy === 'publish' || status.onboarding_status === 'published'}>
                {busy === 'publish' ? 'Verificando...' : status.onboarding_status === 'published' ? 'Loja publicada' : 'Publicar minha loja'}
              </Button>
              {missing.some((item) => ['logo', 'horario'].includes(item)) && (
                <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/admin/admin-lanchonete?from=onboarding')}>Configurar logo e horário</Button>
              )}
              {missing.includes('produto') && (
                <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/admin/produtos')}>Cadastrar produto</Button>
              )}
            </CardContent></Card>
          </Stack>
        </Box>
        <Button
          size="small"
          startIcon={<HelpOutline />}
          onClick={() => { setTutorialStep(0); setTutorialOpen(true); }}
          sx={{ mt: 3, color: 'text.secondary' }}
        >
          Ver tutorial novamente
        </Button>
      </Container>

      <Dialog
        open={tutorialOpen}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 4, overflow: 'visible' } }}
      >
        <Box
          sx={{
            width: 76, height: 76, borderRadius: '50%', bgcolor: '#fff1eb',
            color: '#E50914', display: 'grid', placeItems: 'center',
            mx: 'auto', mt: -4, border: '6px solid #fff'
          }}
        >
          {tutorialSteps[tutorialStep].icon}
        </Box>
        <DialogTitle textAlign="center" sx={{ fontWeight: 900, pb: 1 }}>
          {tutorialSteps[tutorialStep].title}
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {tutorialSteps[tutorialStep].text}
          </Typography>
          <MobileStepper
            variant="dots"
            steps={tutorialSteps.length}
            position="static"
            activeStep={tutorialStep}
            sx={{ justifyContent: 'center', bgcolor: 'transparent', mt: 2, '& .MuiMobileStepper-dots': { mx: 'auto' } }}
            nextButton={null}
            backButton={null}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button color="inherit" onClick={() => finishTutorial(false)}>Pular tutorial</Button>
          <Stack direction="row" spacing={1}>
            {tutorialStep > 0 && <Button onClick={() => setTutorialStep((step) => step - 1)}>Voltar</Button>}
            {tutorialStep < tutorialSteps.length - 1 ? (
              <Button variant="contained" onClick={() => setTutorialStep((step) => step + 1)}>Próximo</Button>
            ) : (
              <Button variant="contained" startIcon={<Storefront />} onClick={() => finishTutorial(true)}>
                Configurar agora
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
